/**
 * ProximityInteractableComponent - Base class for components that need proximity-based
 * interaction detection with players. Provides collision-based and distance-based proximity
 * detection, player tracking, and automatic registration with InteractionComponent.
 */

import * as THREE from 'three';

import { Actor } from '../../actors/Actor.js';
import { Pawn } from '../../actors/Pawn.js';
import { PhysicsMotionType } from '../../physics/PhysicsTypes.js';
import { DefaultCollisionProfile } from '../../systems/collision/CollisionTypes.js';
import { Delegate } from '../../utils/Delegate.js';
import { property } from '../../utils/serialization/decorator.js';
import { CollisionShapeComponent } from '../CollisionShapeComponent.js';
import { SceneComponent } from '../SceneComponent.js';

import type { IInteractable } from './IInteractable.js';
import type { SceneComponentOptions } from '../SceneComponent.js';

export interface ProximityInteractableOptions extends SceneComponentOptions {
  /** Proximity detection range */
  proximityRange?: number;
  /** Whether to use key press interaction or automatic proximity activation */
  useKeyPressInteraction?: boolean;
}

export type ProximityInteractableDelegate = Delegate<[ProximityInteractableComponent], void>;

export abstract class ProximityInteractableComponent extends SceneComponent implements IInteractable {
  @property({ type: 'number', min: 0.5, step: 0.1, decimals: 1, description: 'Proximity detection range' })
  protected proximityRange: number = 2.0;

  @property({ description: 'Whether interaction requires key press (true) or is automatic on proximity (false)' })
  protected useKeyPressInteraction: boolean = true;

  // Interaction components
  protected proximityTrigger: CollisionShapeComponent | null = null;
  protected existingCollisionShape: CollisionShapeComponent | null = null;
  protected playersInRange: Set<Actor> = new Set();
  protected useCollisionBasedProximity: boolean = false;

  // Proximity check optimization
  protected proximityCheckTimer: number = 0;
  protected readonly proximityCheckInterval: number = 0.25; // 4 times per second

  // Event delegates
  public readonly onPlayerEnteredRange: ProximityInteractableDelegate = new Delegate<[ProximityInteractableComponent], void>();
  public readonly onPlayerLeftRange: ProximityInteractableDelegate = new Delegate<[ProximityInteractableComponent], void>();

  public override initialize(options?: ProximityInteractableOptions): void {
    super.initialize(options);
    this.proximityRange = options?.proximityRange ?? this.proximityRange;
    this.useKeyPressInteraction = options?.useKeyPressInteraction ?? this.useKeyPressInteraction;
  }

  public override beginPlay(): void {
    super.beginPlay();
    this.setupProximityDetection();
  }

  public override endPlay(): void {
    super.endPlay();

    // Unregister from all players' InteractionComponents
    for (const player of this.playersInRange) {
      if (player instanceof Pawn) {
        player.getInteractionComponent()?.unregister(this);
      }
    }
    this.playersInRange.clear();
  }

  public override tickPrePhysics(deltaTime: number): void {
    super.tickPrePhysics(deltaTime);

    // Proximity check optimization - only check 4 times per second
    this.proximityCheckTimer += deltaTime;
    if (this.proximityCheckTimer >= this.proximityCheckInterval) {
      this.proximityCheckTimer = 0;
      this.updateProximityCheck();
    }
  }

  //===========================================================================
  // IInteractable Implementation (Abstract)
  //===========================================================================

  public abstract beginInteract(player: Actor): boolean;

  public abstract canInteract(player: Actor): boolean;

  public abstract getInteractionPrompt(): string;

  public abstract getInteractionPriority(): number;

  //===========================================================================
  // Proximity Detection
  //===========================================================================

  /**
   * Sets up proximity detection for interaction.
   * Uses existing collision shape if available, otherwise creates a distance-based trigger.
   */
  protected setupProximityDetection(): void {
    const actor = this.getActor();
    if (!actor) return;

    // Check for existing CollisionShapeComponent
    const existingCollisionShape = actor.rootComponent.getComponent(CollisionShapeComponent);
    if (existingCollisionShape) {
      this.existingCollisionShape = existingCollisionShape;
      this.useCollisionBasedProximity = true;
      this.setupCollisionBasedProximity();
    } else {
      this.useCollisionBasedProximity = false;
      this.setupDistanceBasedProximity();
    }
  }

  /**
   * Sets up collision-based proximity detection using existing CollisionShapeComponent.
   */
  protected setupCollisionBasedProximity(): void {
    if (!this.existingCollisionShape) return;

    // Bind to overlap events for proximity detection
    this.existingCollisionShape.onOverlapWith.add((thisComponent, otherComponent) => {
      const otherActor = otherComponent.getActor();
      if (otherActor && this.isPlayerPawn(otherActor)) {
        this.handlePlayerEnteredRange(otherActor);
      }
    });

    // Bind to overlap end events for proximity exit detection
    this.existingCollisionShape.onStopOverlappingWith.add((thisComponent, otherComponent) => {
      const otherActor = otherComponent.getActor();
      if (otherActor && this.isPlayerPawn(otherActor)) {
        this.handlePlayerLeftRange(otherActor);
      }
    });
  }

  /**
   * Sets up distance-based proximity detection with a new proximity trigger.
   */
  protected async setupDistanceBasedProximity(): Promise<void> {
    const actor = this.getActor();
    if (!actor) return;

    const world = actor.getWorld();
    if (!world) {
      console.warn(`[${this.constructor.name}] Could not get world to add trigger`);
      return;
    }

    // Calculate trigger position based on actor position
    const triggerPosition = this.calculateTriggerPosition();

    // Create the trigger component with box geometry
    this.proximityTrigger = CollisionShapeComponent.create({
      geometry: new THREE.BoxGeometry(1, 1, 1),
      scale: new THREE.Vector3(
        this.proximityRange * 2,
        2.5,
        this.proximityRange * 2
      ),
      physicsOptions: {
        enabled: true,
        motionType: PhysicsMotionType.Static,
        generateCollisionEvents: true,
        collisionProfile: DefaultCollisionProfile.Trigger
      }
    });

    // Bind to overlap events
    this.proximityTrigger.onOverlapWith.add((thisComponent, otherComponent) => {
      const otherActor = otherComponent.getActor();
      if (otherActor && this.isPlayerPawn(otherActor)) {
        this.handlePlayerEnteredRange(otherActor);
      }
    });

    // Bind to overlap end events
    this.proximityTrigger.onStopOverlappingWith.add((thisComponent, otherComponent) => {
      const otherActor = otherComponent.getActor();
      if (otherActor && this.isPlayerPawn(otherActor)) {
        this.handlePlayerLeftRange(otherActor);
      }
    });

    // Create an actor to hold the trigger component
    const triggerActor = Actor.create({
      position: triggerPosition,
      rootComponent: SceneComponent.create({})
    });

    // Add the trigger as a child component
    triggerActor.rootComponent.add(this.proximityTrigger);

    // Add to world
    world.addActors(triggerActor);

    // Switch to collision-based proximity since we're now using collision events
    this.useCollisionBasedProximity = true;
  }

  /**
   * Calculates the world position for the proximity trigger.
   * Subclasses can override this to customize trigger placement.
   */
  protected calculateTriggerPosition(): THREE.Vector3 {
    const actor = this.getActor();
    if (!actor) return new THREE.Vector3();

    const triggerPosition = actor.rootComponent.position.clone();
    triggerPosition.y += 1.25; // Center vertically
    return triggerPosition;
  }

  /**
   * Checks if an actor is a player pawn (controlled by a PlayerController).
   */
  protected isPlayerPawn(actor: Actor): boolean {
    return actor.getPlayerController() !== null;
  }

  /**
   * Handles when a player enters the interaction range.
   * Can be overridden by subclasses for custom behavior.
   */
  protected handlePlayerEnteredRange(player: Actor): void {
    const wasFirstPlayer = this.playersInRange.size === 0;
    this.playersInRange.add(player);

    // Register with player's InteractionComponent if using key press interaction
    if (this.useKeyPressInteraction && player instanceof Pawn) {
      player.getInteractionComponent()?.register(this);
    }

    // Notify subclasses
    this.onPlayerEntered(player);

    // Fire delegate
    this.onPlayerEnteredRange.invoke(this);

    // If first player entered, notify subclass
    if (wasFirstPlayer) {
      this.onFirstPlayerEntered();
    }
  }

  /**
   * Handles when a player leaves the interaction range.
   * Can be overridden by subclasses for custom behavior.
   */
  protected handlePlayerLeftRange(player: Actor): void {
    this.playersInRange.delete(player);

    // Unregister from player's InteractionComponent
    if (player instanceof Pawn) {
      player.getInteractionComponent()?.unregister(this);
    }

    // Notify subclasses
    this.onPlayerLeft(player);

    // Fire delegate
    this.onPlayerLeftRange.invoke(this);

    // If last player left, notify subclass
    if (this.playersInRange.size === 0) {
      this.onLastPlayerLeft();
    }
  }

  /**
   * Called when a player enters range. Override for custom behavior.
   */
  protected onPlayerEntered(player: Actor): void {
    // Subclass hook - no-op by default
  }

  /**
   * Called when a player leaves range. Override for custom behavior.
   */
  protected onPlayerLeft(player: Actor): void {
    // Subclass hook - no-op by default
  }

  /**
   * Called when the first player enters range. Override for custom behavior.
   */
  protected onFirstPlayerEntered(): void {
    // Subclass hook - no-op by default
  }

  /**
   * Called when the last player leaves range. Override for custom behavior.
   */
  protected onLastPlayerLeft(): void {
    // Subclass hook - no-op by default
  }

  /**
   * Proximity check - handles both collision-based and distance-based detection.
   */
  protected updateProximityCheck(): void {
    if (!this.useCollisionBasedProximity) {
      // For distance-based proximity, do full distance calculation
      this.updateDistanceBasedProximity();
    }
    // For collision-based proximity, detection is handled entirely by collision events
  }

  /**
   * Updates distance-based proximity detection.
   */
  protected updateDistanceBasedProximity(): void {
    if (!this.proximityTrigger) return;

    const world = this.getActor()?.getWorld();
    if (!world) return;

    const triggerPosition = this.proximityTrigger.getWorldPosition(new THREE.Vector3());
    const range = this.proximityRange;

    // Clear current players in range
    this.playersInRange.clear();

    // Check all actors in the world that are player pawns
    const playerActors = world.getActorsByPredicate(actor => this.isPlayerPawn(actor));

    for (const actor of playerActors) {
      const actorPosition = actor.rootComponent.getWorldPosition(new THREE.Vector3());
      const distance = triggerPosition.distanceTo(actorPosition);

      if (distance <= range) {
        this.playersInRange.add(actor);
      }
    }
  }

  //===========================================================================
  // Utility Methods
  //===========================================================================

  /**
   * Returns true if any players are currently in range.
   */
  public hasPlayersInRange(): boolean {
    return this.playersInRange.size > 0;
  }

  /**
   * Returns the number of players currently in range.
   */
  public getPlayerCountInRange(): number {
    return this.playersInRange.size;
  }

  /**
   * Returns an iterator over players currently in range.
   */
  public getPlayersInRange(): IterableIterator<Actor> {
    return this.playersInRange.values();
  }

  /**
   * Checks if a specific player is in range.
   */
  public isPlayerInRange(player: Actor): boolean {
    return this.playersInRange.has(player);
  }
}
